/**
 * @fileDescription Product Form Landing page
 * @author Pankaj Kulshreshtha | Cheers Interactive
 * @date 20/Aug/2020 * @lastModified 10/Jun/2025
 * @lastModifiedBy Rakesh Seervi | Cheers Interactive
 */

import React, { useEffect, useState, useRef, useMemo } from "react";
import Tabs from "../../../../../components/tabsComponent";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import { getLoggedInUser, getProductVariant, accessRightActionCheck, accessRightInterfaceCheck } from "../../../../../../utilities";
import { setBreadCrumb } from "../../../../../../middleware/actions/breadCrumbAction";
import { actionError, showMessage, actionSuccess } from "../../../../../../middleware/actions/utilityAction";
import Overview from "./overview";
import TechSpecification from "./technical-specification";
import Taxonomy from "./taxonomy-tags";
import AccessDenied from "../../../../../components/accessDenied";
import { fetchProductBasicById, addProductBasic, editProductBasic } from "../../../../../../middleware/services/productApi";
import { fetchSingleCompany } from "../../../../../../middleware/services/companyApi";
import { processingSubmitProduct, processingFinalSubmitProduct } from "../../../../../../middleware/services/cmsApi";
import { showAlertBox } from "../../../../../../middleware/actions/alertBoxAction";
import { setSideNavForcedActiveLink } from "../../../../../../middleware/actions/sideNavAction";
import { PRODUCT_VARIANT } from "../../../../../../constants";
import PropTypes from "prop-types";

const successMsg = {
  overview: "Product saved successfully.",
  tech_specificaion: "Technical Specification details submitted successfully.",
};

const ProductForm = (props) => {
  const { accessRights: aR } = props;
  const { currentIndex = 0 } = props.location.state || {};
  const dispatch = useDispatch();
  const tabRef = useRef();
  const [selectedIndex, setSelectedIndex] = useState(0);
  const history = useHistory();
  const split_url = props.location.pathname.split("/");
  let productId = props.computedMatch?.params?.productId || "";
  const companyId = props.computedMatch?.params?.companyId || "";
  const editData = productId && productId.length === 24;
  const [productName, setProductName] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [interfaceActionAccess, setInterfaceActionAccess] = useState([]);
  const [saveTrendTreeData, setSaveTrendTreeData] = useState(false);

  const productVariant = getProductVariant();
  let TABS_NAME = ["Overview", "Technical Specification"];
  const hashArr = ["Overview", "Technical Specification", "Taxonomy"];

  productVariant === PRODUCT_VARIANT.INSIDER ? TABS_NAME.push("Taxonomy & Tags") : TABS_NAME.push("Taxonomy");

  split_url.pop();
  split_url.pop();
  if (editData) split_url.pop();
  const backPath = companyId && companyId.length === 24 ? split_url.join("/") + "/edit#product" : "/product";
  split_url.pop();
  const dataObj = {
    companyId: companyId,
    productId: productId,
    editData: editData,
    backPath: backPath,
  };
  // split_url.splice(0,-3)

  const breadCrumbLinks = useMemo(() => {
    const links = [{ linkUrl: split_url.join("/"), linkName: `Company`, linkActive: false }];

    if (!companyId || companyName === "") return links;

    links.push({ linkUrl: backPath, linkName: companyName, linkActive: false });
    links.push({ linkUrl: backPath, linkName: aR.moduleName, linkActive: false });
    if (editData && productName) {
      links.push({
        linkUrl: `${props.location.pathname}#overview`,
        linkName: productName || "Add New",
        linkActive: false,
      });
      links.push({
        linkUrl: ``,
        linkName: hashArr[selectedIndex],
        linkActive: true,
      });
    }

    if (!editData)
      links.push({
        linkUrl: ``,
        linkName: "Add New",
        linkActive: true,
      });

    return links;
  }, [companyName, productName, selectedIndex]);

  const loggedInUser = getLoggedInUser();

  const created = (e) => {
    tabRef.current.animation.previous = { effect: "None" };
    tabRef.current.animation.next = { effect: "None" };

    if (!productId) {
      tabRef.current.enableTab(1, false);
      tabRef.current.enableTab(2, false);
      tabRef.current.select(0);
    } else {
      tabRef.current.enableTab(1, true);
      tabRef.current.enableTab(2, true);
      tabRef.current.select(selectedIndex);
    }
  };

  useEffect(() => {
    setSelectedIndex(currentIndex);
    setTimeout(() => {
      tabRef.current.select(currentIndex);
    }, 0);
  }, [currentIndex]);

  const fetchData = async () => {
    await getCompanyName();
    await getProductName();
  };

  useEffect(() => {
    fetchData();
    dispatch(setBreadCrumb(breadCrumbLinks));
    dispatch(setSideNavForcedActiveLink("/product"));
    return () => {
      dispatch(setSideNavForcedActiveLink());
      dispatch(setBreadCrumb());
    };
  }, [dispatch, selectedIndex, props.location.hash, breadCrumbLinks]);

  const onSaveData = async (data) => {
    try {
      const formData = new FormData();
      for (let key in data) {
        formData.append(key, data[key]);
      }
      const productVariant = getProductVariant();
      if (productVariant) {
        formData.append("productVariant", JSON.stringify([{ productName: productVariant, isLive: "NO" }]));
      }
      const result = await addProductBasic(formData);
      if (result.status === 200) {
        dispatch(actionSuccess("Product added successfully."));
        tabRef.current.enableTab(1, true);
        tabRef.current.enableTab(2, true);
        tabRef.current.select(0);
        return {
          status: "success",
          companyId: companyId,
          productId: result.data.data.id,
          editData: false,
        };
      } else if (result.statusCode === 409) {
        dispatch(showMessage("Product Already Exists.", "warning"));
        return { status: "failed" };
      } else {
        dispatch(actionError("SOMETHING WENT WRONG."));
        return { status: "failed" };
      }
    } catch (err) {
      if (err.data?.statusCode === 409) {
        dispatch(showMessage(`Product Already Exists.`, "warning"));
      } else {
        dispatch(actionError(err ? err.data.message : "SOMETHING WENT WRONG."));
      }
      return { status: "failed" };
    }
  };

  const onSaveProcessingData = (tab, payload = {}) => {
    payload.userId = loggedInUser._id;
    if (!productId) {
      productId = payload.productId;
      delete payload.productId;
    }

    processingSubmitProduct(productId, tab, payload)
      .then((response) => {
        if (response.status === 200) {
          dispatch(actionSuccess(successMsg[tab]));
          return { status: "success" };
        }
      })
      .catch((err) => {
        dispatch(actionError(err.data.message || "Something went wrong."));
      });
  };

  const onFinalSaveProcessingData = (payload = {}) => {
    payload.userId = loggedInUser._id;
    dispatch(
      showAlertBox({
        okCallback: async () => {
          if (productId) {
            try {
              const response = await processingFinalSubmitProduct(productId, payload);
              if (response.status === 200) {
                dispatch(actionSuccess(`Product submitted successfully.`));
                history.push(backPath, { companyId: companyId });
                return { status: "success" };
              }
            } catch (err) {
              dispatch(actionError(err.data.message || "Something went wrong."));
            }
          } else {
            dispatch(actionError("Please save the product before Submit."));
          }
        },
        okText: "Submit",
        cancelText: "Cancel",
        content: "Are you sure you want to submit ?",
        title: "dialogAlertCss",
      })
    );
  };

  const getProductName = async () => {
    let fields = { fields: ["id", "productName"] };
    if (productId) {
      const response = await fetchProductBasicById(productId, { ...fields });
      setProductName(response.data.data.productName);
    }
  };
  const getCompanyName = async () => {
    let fields = { fields: ["id", "companyName"] };
    if (companyId) {
      try {
        const response = await fetchSingleCompany(companyId, { ...fields });
        setCompanyName(response.data.data.companyName);
      } catch (error) {
        dispatch(actionError(error.data.message || "something went wrong."));
      }
    }
  };

  const onUpdateData = async (data) => {
    const formData = new FormData();
    for (let key in data) {
      formData.append(key, data[key]);
    }

    try {
      await editProductBasic(data.id, formData);
      dispatch(actionSuccess("Product updated successfully."));
      return {
        status: "success",
        companyId: companyId,
        productId: data.id,
        editData: true,
      };
    } catch (err) {
      dispatch(actionError(err ? err.data.message : "SOMETHING WENT WRONG."));
      return { status: "failed" };
    }
  };

  /* Interface access  */
  const accessRightInterfaceRights = accessRightInterfaceCheck(aR.moduleName, hashArr);

  /* Interface functionality wise access  */
  useEffect(() => {
    let interfaceName = hashArr[selectedIndex];
    let actionAccess = accessRightActionCheck(aR.moduleName, interfaceName);

    setInterfaceActionAccess(actionAccess);
  }, [selectedIndex]);

  let interfaceAccess = accessRightInterfaceRights.includes(hashArr[selectedIndex]);

  return (
    <div style={{ background: "#F2F2F2", overflow: "hidden" }} className="gennx-envelope company-master">
      <Tabs
        tabsName={TABS_NAME}
        tabRef={tabRef}
        created={created}
        handleClick={() => {
          setSelectedIndex(tabRef.current.selectedItem);
        }}
        cssClass="newHeaderTabCss  company-tabs-css"
      >
        <div key="productOverview" className="company-tab-content">
          {selectedIndex === 0 &&
            (interfaceAccess ? (
              <Overview
                onSaveProcessingData={onSaveProcessingData}
                onFinalSaveProcessingData={onFinalSaveProcessingData}
                onUpdateData={onUpdateData}
                onSaveData={onSaveData}
                {...props}
                {...dataObj}
                editData={editData}
                productId={productId}
                selectedIndex={selectedIndex}
                setCurrentTab={(selectedIndex) => {
                  tabRef.current.select(selectedIndex);
                  setSelectedIndex(selectedIndex);
                }}
                hashArr={hashArr}
                saveTrendTreeData={saveTrendTreeData}
                setSaveTrendTreeData={setSaveTrendTreeData}
              />
            ) : (
              <AccessDenied />
            ))}
        </div>
        <div key="productSpecification" className="company-tab-content">
          {selectedIndex == 1 &&
            (interfaceAccess ? (
              <TechSpecification
                onSaveProcessingData={onSaveProcessingData}
                {...props}
                {...dataObj}
                selectedIndex={selectedIndex}
                setCurrentTab={(selectedIndex) => {
                  tabRef.current.select(selectedIndex);
                  setSelectedIndex(selectedIndex);
                }}
                hashArr={hashArr}
                interfaceActionAccess={interfaceActionAccess}
              />
            ) : (
              <AccessDenied />
            ))}
        </div>

        <div key="productTaxonomy">
          {selectedIndex === 2 &&
            (interfaceAccess ? (
              <Taxonomy
                onSaveProcessingData={onSaveProcessingData}
                {...props}
                {...dataObj}
                selectedIndex={selectedIndex}
                setCurrentTab={(selectedIndex) => {
                  tabRef.current.select(selectedIndex);
                  setSelectedIndex(selectedIndex);
                }}
                hashArr={hashArr}
                interfaceActionAccess={interfaceActionAccess}
                saveTrendTreeData={saveTrendTreeData}
                setSaveTrendTreeData={setSaveTrendTreeData}
              />
            ) : (
              <AccessDenied />
            ))}
        </div>
      </Tabs>
    </div>
  );
};
ProductForm.propTypes = {
  accessRights: PropTypes.object,
  newsId: PropTypes.string,
  location: PropTypes.object,
  computedMatch: PropTypes.object,
  editData: PropTypes.bool,
};
export default ProductForm;
